// Talimatlar + Nasil Kullanilir sayfalari — qr.emanetx.com'da yayinlanir.
// Mobile WebView modal'i da bu sayfalari acar (iki dilli, ?lang=tr|en).
// Icerik buradan guncellenir, uygulama store update gerekmez.

import type { ReactNode } from 'react';
import { EPOSTA, TELEFON } from './yasal.js';

// === Dil tespiti — ?lang=tr|en query (default tr) ===

function dilOku(): 'tr' | 'en' {
  if (typeof window === 'undefined') return 'tr';
  const u = new URLSearchParams(window.location.search);
  const v = (u.get('lang') ?? '').toLowerCase();
  return v === 'en' ? 'en' : 'tr';
}

// === Ortak cerceve (dil bagimsiz tasarim) ===

function Cerceve({
  baslik,
  alt,
  digerDilEtiket,
  digerDilYol,
  children,
}: {
  baslik: string;
  alt?: string;
  digerDilEtiket: string;
  digerDilYol: string;
  children: ReactNode;
}) {
  return (
    <main className="min-h-screen bg-zinc-50">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <header className="border-b border-zinc-200 pb-6">
          <div className="flex items-center justify-between">
            <a
              href="/"
              className="inline-flex items-center text-sm font-bold text-zinc-500 hover:text-zinc-900"
            >
              ← emanet<span className="text-amber-500">x</span>
            </a>
            <a
              href={digerDilYol}
              className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-bold text-zinc-700 hover:bg-zinc-100"
            >
              {digerDilEtiket}
            </a>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-zinc-900">{baslik}</h1>
          {alt && <p className="mt-1 text-sm text-zinc-500">{alt}</p>}
        </header>

        <article className="prose prose-zinc mt-8 max-w-none text-zinc-700">{children}</article>

        <footer className="mt-16 border-t border-zinc-200 pt-6 text-center text-xs text-zinc-400">
          © {new Date().getFullYear()} EmanetX
          <span className="px-2">·</span>
          <a className="hover:text-zinc-600" href={`mailto:${EPOSTA}`}>
            {EPOSTA}
          </a>
        </footer>
      </div>
    </main>
  );
}

const h2 = 'mt-8 text-xl font-extrabold text-zinc-900';
const h3 = 'mt-5 font-extrabold text-zinc-900';
const p = 'mt-3 leading-relaxed';
const ol = 'mt-3 list-decimal space-y-2 pl-6';
const ul = 'mt-3 list-disc space-y-1 pl-6';
const adimKart = 'mt-4 flex gap-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm';
const adimNo =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 font-extrabold text-zinc-900';

// === Nasil Kullanilir ===

export function NasilKullanilir() {
  const dil = dilOku();
  if (dil === 'en') return <NasilKullanilirEN />;
  return <NasilKullanilirTR />;
}

function NasilKullanilirTR() {
  return (
    <Cerceve
      baslik="Nasıl Kullanılır?"
      alt="EmanetX akıllı dolap adım adım rehber"
      digerDilEtiket="EN"
      digerDilYol="/nasil-kullanilir?lang=en"
    >
      <p className={p}>
        EmanetX akıllı dolaplar, eşyalarınızı güvenle saklamanız için 7/24 hazır. Dakikalar içinde
        kiralayın, ihtiyaç bittiğinde alın.
      </p>

      <h2 className={h2}>Adım adım kullanım</h2>

      <div className={adimKart}>
        <div className={adimNo}>1</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Yakın bir nokta seçin</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Uygulamada haritada müsait dolapları görebilirsiniz. Konumunuza en yakın noktayı seçin,
            "Yol Tarifi" ile gitmek istediğinizde harita uygulaması açılır.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>2</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Eşya boyutunu seçin</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Küçük (sırt çantası), Orta (valiz) veya Büyük (büyük bavul) seçeneklerinden eşyanıza
            uygun olanı seçin. Müsait dolap numarası otomatik atanır.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>3</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Süre belirleyin</h3>
          <p className="mt-1 text-sm leading-relaxed">
            1, 2, 4, 8 veya 24 saatten birini seçin. Periyot ücreti peşin alınır, süreyi aşarsanız
            saatlik ek ücret işler. İhtiyacınız bitince dolabı sonlandırabilirsiniz.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>4</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Ödemeyi yapın</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Cüzdanınızdan veya kartınızla 3D Secure ile ödeyin. Kart bilgileriniz bizde saklanmaz —
            Akbank güvenli ödeme altyapısı kullanılır.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>5</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Dolap açılır, eşyanızı koyun</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Ödeme başarılı olunca dolap otomatik açılır. Eşyanızı yerleştirin, kapağı tam olarak
            kapatın — kilit otomatik devreye girer.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>6</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Eşyanızı geri alın</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Geri döndüğünüzde uygulamada "Aç" butonuna basın. Dolap açılır, eşyanızı alın, kapağı
            kapatın — kiralama otomatik sonlanır.
          </p>
        </div>
      </div>

      <h2 className={h2}>İlk 5 dakika geçici açma</h2>
      <p className={p}>
        Eşyanızı yerleştirdikten sonra ilk 5 dakika içinde dolabı tekrar açma şansınız var (örneğin
        bir şey eklemek istediyseniz). Bu süreden sonra ek bir açma kullanım periyodunu sonlandırır.
      </p>

      <h2 className={h2}>Süre uzatma</h2>
      <p className={p}>
        Süre bitmeden uygulamadan +1 saat / +1 gün ekleyebilirsiniz. Süre dolduktan sonra her saat
        ek ücret işler — ek ücret ödenmeden dolap açılmaz.
      </p>

      <h2 className={h2}>Sorun çözme</h2>
      <ul className={ul}>
        <li>
          <strong>Dolap açılmadı:</strong> "Yardım — Dolabım Açılmadı" butonunu kullanın. Görevli
          uzaktan açma yapabilir.
        </li>
        <li>
          <strong>Eşyamı geri alamıyorum:</strong> Aktif kiralamanız bittiyse destek ekibine ulaşın,
          kimliğinizi doğrulayarak dolabı açabiliriz.
        </li>
        <li>
          <strong>Ödeme yapılamadı:</strong> Kart limitinizi kontrol edin, 3D Secure SMS'ini girin.
          Sorun devam ederse Cüzdan'a yükleyip oradan ödeyebilirsiniz.
        </li>
      </ul>

      <h2 className={h2}>Yardım & İletişim</h2>
      <p className={p}>
        7/24 destek:{' '}
        <a className="text-amber-600 underline" href={`tel:${TELEFON}`}>
          {TELEFON}
        </a>
        <br />
        E-posta:{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
      </p>
    </Cerceve>
  );
}

function NasilKullanilirEN() {
  return (
    <Cerceve
      baslik="How to Use"
      alt="EmanetX smart locker step-by-step guide"
      digerDilEtiket="TR"
      digerDilYol="/nasil-kullanilir?lang=tr"
    >
      <p className={p}>
        EmanetX smart lockers keep your belongings safe 24/7. Rent in minutes, pick up whenever
        you're done.
      </p>

      <h2 className={h2}>Step by step</h2>

      <div className={adimKart}>
        <div className={adimNo}>1</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Pick a nearby location</h3>
          <p className="mt-1 text-sm leading-relaxed">
            See available lockers on the map. Pick the closest one — tap "Directions" to launch your
            map app.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>2</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Choose your size</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Small (backpack), Medium (suitcase) or Large (big luggage). An available locker is
            automatically assigned.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>3</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Set duration</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Pick 1, 2, 4, 8 or 24 hours. Base fee is charged upfront; additional hours are billed
            hourly. You can end the rental early once you're done.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>4</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Pay securely</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Pay from your wallet or with your card via 3D Secure. We never store card details —
            Akbank secure payment is used.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>5</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Place your belongings</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Locker opens automatically after payment. Place your items, close the door — the lock
            engages automatically.
          </p>
        </div>
      </div>

      <div className={adimKart}>
        <div className={adimNo}>6</div>
        <div>
          <h3 className="text-base font-extrabold text-zinc-900">Pick up</h3>
          <p className="mt-1 text-sm leading-relaxed">
            Tap "Open" in the app when you return. Locker opens, take your items, close the door —
            rental ends automatically.
          </p>
        </div>
      </div>

      <h2 className={h2}>First 5-minute re-open</h2>
      <p className={p}>
        You can re-open the locker within the first 5 minutes after closing (e.g. to add something).
        Re-opening after that ends your rental.
      </p>

      <h2 className={h2}>Extending duration</h2>
      <p className={p}>
        You can add +1h / +1d from the app before time runs out. Going overtime adds hourly charges
        — the locker won't open until they're paid.
      </p>

      <h2 className={h2}>Troubleshooting</h2>
      <ul className={ul}>
        <li>
          <strong>Locker didn't open:</strong> Use "Help — My locker didn't open." Staff can open it
          remotely.
        </li>
        <li>
          <strong>Can't retrieve my items:</strong> If your rental ended, contact support — we can
          verify your identity and open the locker.
        </li>
        <li>
          <strong>Payment failed:</strong> Check card limit, complete the 3D Secure SMS. If it keeps
          failing, top up your wallet and pay from there.
        </li>
      </ul>

      <h2 className={h2}>Help & Contact</h2>
      <p className={p}>
        24/7 support:{' '}
        <a className="text-amber-600 underline" href={`tel:${TELEFON}`}>
          {TELEFON}
        </a>
        <br />
        Email:{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
      </p>
    </Cerceve>
  );
}

// === Talimatlar ===

export function Talimatlar() {
  const dil = dilOku();
  if (dil === 'en') return <TalimatlarEN />;
  return <TalimatlarTR />;
}

function TalimatlarTR() {
  return (
    <Cerceve
      baslik="Kullanım Talimatları"
      alt="Lütfen dolabı kullanmadan önce okuyun"
      digerDilEtiket="EN"
      digerDilYol="/talimatlar?lang=en"
    >
      <h2 className={h2}>Genel kurallar</h2>
      <ul className={ul}>
        <li>Dolaba yalnızca yasal, tehlikesiz ve dayanıklı eşya bırakın.</li>
        <li>Kapağı kapatmadan önce kilidin devreye girdiğini kontrol edin.</li>
        <li>Eşyanızı süre bitmeden alın, ek ücret işlemesin.</li>
        <li>PIN kodunuzu kimseyle paylaşmayın.</li>
      </ul>

      <h2 className={h2}>Kesinlikle bırakılmayacaklar</h2>
      <p className={p}>
        Aşağıdaki maddeler dolaba kesinlikle bırakılamaz. Aksi durumda yasal sorumluluk size aittir.
      </p>
      <h3 className={h3}>1. Yasa dışı maddeler</h3>
      <p className={p}>
        Uyuşturucu, ateşli silah, patlayıcı, çalıntı eşya, yasa dışı her tür materyal.
      </p>
      <h3 className={h3}>2. Tehlikeli maddeler</h3>
      <p className={p}>
        Yanıcı, parlayıcı, aşındırıcı, zehirli, radyoaktif maddeler; basınçlı kaplar (sprey, tüp
        vb.).
      </p>
      <h3 className={h3}>3. Canlı varlıklar ve gıda</h3>
      <p className={p}>Hayvan, bitki; bozulabilen veya koku yapan yiyecek/içecek.</p>
      <h3 className={h3}>4. Çok değerli eşyalar</h3>
      <p className={p}>
        Nakit, mücevher, değerli maden, menkul kıymet. Kaybından şirketimiz sorumlu tutulamaz.
      </p>
      <h3 className={h3}>5. Hassas belgeler</h3>
      <p className={p}>Pasaport, kimlik, resmi evrak.</p>

      <h2 className={h2}>Ücretlendirme</h2>
      <ul className={ul}>
        <li>Periyot ücreti peşin alınır (örn. 24 saat).</li>
        <li>Süre aşımında saatlik ek ücret işler.</li>
        <li>Ek ücret ödenmeden dolap açılmaz.</li>
        <li>Erken sonlandırmada iade yapılmaz — dolap size tahsis edilmiştir.</li>
      </ul>

      <h2 className={h2}>Unutulan eşyalar</h2>
      <p className={p}>
        15 gün içinde alınmayan eşyalar şirket merkezine taşınır ve 30 gün daha saklanır. 45 gün
        sonunda alınmayan eşyalar imha edilebilir veya bağışlanabilir.
      </p>

      <h2 className={h2}>Sorumluluk</h2>
      <p className={p}>
        Şirket, dolabın fiziksel güvenliğini sağlar ancak sigorta şirketi değildir. Eşyaların
        içeriğinden, değerinden veya uğrayabileceği zararlardan sorumlu değildir.
      </p>

      <h2 className={h2}>Daha fazlası</h2>
      <p className={p}>
        Detaylı koşullar için{' '}
        <a className="text-amber-600 underline" href="/sozlesme">
          Kullanıcı Sözleşmesi
        </a>
        'ne bakınız.
      </p>
    </Cerceve>
  );
}

function TalimatlarEN() {
  return (
    <Cerceve
      baslik="User Instructions"
      alt="Please read before using a locker"
      digerDilEtiket="TR"
      digerDilYol="/talimatlar?lang=tr"
    >
      <h2 className={h2}>General rules</h2>
      <ul className={ul}>
        <li>Only legal, non-hazardous, durable items may be stored.</li>
        <li>Confirm the lock engages before walking away.</li>
        <li>Pick up before time runs out to avoid overtime charges.</li>
        <li>Never share your PIN.</li>
      </ul>

      <h2 className={h2}>Strictly prohibited items</h2>
      <p className={p}>
        Items below cannot be stored in lockers. Legal responsibility is solely yours.
      </p>
      <h3 className={h3}>1. Illegal items</h3>
      <p className={p}>Drugs, firearms, explosives, stolen goods, any unlawful material.</p>
      <h3 className={h3}>2. Hazardous materials</h3>
      <p className={p}>
        Flammable, corrosive, toxic or radioactive items; pressurised containers (spray, gas
        cylinders).
      </p>
      <h3 className={h3}>3. Live beings & food</h3>
      <p className={p}>Animals, plants; perishable or odorous food/drink.</p>
      <h3 className={h3}>4. High-value items</h3>
      <p className={p}>
        Cash, jewellery, precious metals, securities. The company is not liable for their loss.
      </p>
      <h3 className={h3}>5. Sensitive documents</h3>
      <p className={p}>Passport, ID card, official documents.</p>

      <h2 className={h2}>Pricing</h2>
      <ul className={ul}>
        <li>Base period fee is charged upfront (e.g. 24 hours).</li>
        <li>Overtime is billed hourly.</li>
        <li>Locker won't open until overtime is paid.</li>
        <li>No refunds on early termination — the locker was reserved for you.</li>
      </ul>

      <h2 className={h2}>Forgotten items</h2>
      <p className={p}>
        Items not collected within 15 days are moved to our central facility and kept 30 more days.
        After 45 days unclaimed items may be disposed of or donated.
      </p>

      <h2 className={h2}>Liability</h2>
      <p className={p}>
        We secure the locker physically but are not an insurance provider. Contents, value or damage
        to your items are not the company's responsibility.
      </p>

      <h2 className={h2}>More</h2>
      <p className={p}>
        For full terms see the{' '}
        <a className="text-amber-600 underline" href="/sozlesme">
          User Agreement
        </a>
        .
      </p>
    </Cerceve>
  );
}

// === Router yardimcisi ===

export function YardimRota({ yol }: { yol: string }): ReactNode | null {
  switch (yol) {
    case '/nasil-kullanilir':
      return <NasilKullanilir />;
    case '/talimatlar':
      return <Talimatlar />;
    default:
      return null;
  }
}
